import { OfferView } from "@/client/components/offer-view";
import { UpRightButton } from "@/client/components/up-right-button";
import { useStore } from "@/client/store";
import { type CreditCard, type Offer } from "@/data/schemas";
import {
  getMaxValueOffer,
  getOfferFirstYearValue,
  percentOfHistoricalMax,
} from "@/utils/calculations";
import { parseDate } from "@/utils/date";
import { enumToText } from "@/utils/enums";
import { offerMatchesFilters } from "@/utils/filter";
import { cardCountsTowards524 } from "@/utils/fiveTwentyFour";
import {
  Anchor,
  Badge,
  Box,
  Button,
  Card,
  Group,
  Image,
  Space,
  Stack,
  Text,
  Title,
  useMantineTheme,
} from "@mantine/core";
import { IconQuestionMark } from "@tabler/icons-react";
import va from "@vercel/analytics";
import { useMemo } from "react";

export const CardView = ({ card }: { card: CreditCard }) => {
  const theme = useMantineTheme();
  const filters = useStore((state) => state.filters);

  const percentage = useMemo(
    () => percentOfHistoricalMax(getMaxValueOffer(card) as Offer, card),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- getMaxValueOffer/percentOfHistoricalMax read filters via getStore(); filters must be listed to recompute when valuation changes
    [card, filters],
  );

  const countsTowards524 = useMemo(() => {
    return cardCountsTowards524(card);
  }, [card]);

  // eslint-disable-next-line react-hooks/exhaustive-deps -- getMaxValueOffer reads filters via getStore(); filters must be listed to recompute when valuation changes
  const maxValueOffer = useMemo(() => getMaxValueOffer(card), [card, filters]);
  const maxValueOfferDetails = useMemo(() => {
    if (!maxValueOffer)
      return {
        explanation:
          "No valid offers. This usually means one expired recently. The new one will be added soon!",
        value: 0,
      };
    return getOfferFirstYearValue(maxValueOffer, card);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- getOfferFirstYearValue reads filters via getStore(); filters must be listed to recompute when valuation changes
  }, [maxValueOffer, card, filters]);

  const currentOffers = card.offers
    .filter((offer) => offerMatchesFilters(offer, filters, card))
    .filter(
      (offer) =>
        !offer.expiration ||
        (offer.expiration && parseDate(offer.expiration) > new Date()),
    );

  return (
    <Card
      shadow="sm"
      p="sm"
      pb="65px"
      radius={"md"}
      withBorder={true}
      style={{ position: "relative" }}
    >
      <Card.Section>
        <Image
          src={card.imageUrl}
          height={120}
          alt={`${enumToText(card.issuer)} ${card.name}`}
        />
      </Card.Section>
      <Group justify={"space-between"}>
        <Box py="sm" style={{ maxWidth: "calc(100% - 130px)", height: "100%" }}>
          <Title order={4}>{card.name}</Title>
          <Text size={"sm"}>{enumToText(card.issuer)}</Text>
        </Box>

        <Box right={"right"}>
          <Text size={"sm"}>First-Year Value</Text>
          <Group gap={"xs"} justify={"right"}>
            <IconQuestionMark
              size={14}
              style={{ cursor: "pointer" }}
              onClick={() => alert(maxValueOfferDetails.explanation)}
            />
            <Title order={2}>${maxValueOfferDetails.value}</Title>
          </Group>
        </Box>
      </Group>
      {card.details && (
        <Text size={"sm"} mb="xs">
          <b>NOTE:</b>
          {" " + card.details}
        </Text>
      )}
      <Group gap={"xs"}>
        <Badge
          radius={0}
          color={
            card.annualFee === 0 || card.isAnnualFeeWaived
              ? "green"
              : card.annualFee <= 100
                ? "yellow"
                : card.annualFee <= 300
                  ? "orange"
                  : "red"
          }
          variant="light"
        >
          AF: ${card.annualFee} {card.isAnnualFeeWaived && "(waived)"}
        </Badge>
        <Badge
          radius={0}
          color={countsTowards524 ? "yellow" : "green"}
          variant="light"
        >
          {countsTowards524 && "5/24: Counts"}
          {!countsTowards524 && "5/24: Doesn't Count"}
        </Badge>
        <Badge
          radius={0}
          color={
            percentage >= 0.94
              ? "green"
              : percentage >= 0.8
                ? "yellow"
                : percentage >= 0.6
                  ? "orange"
                  : "red"
          }
          variant="light"
        >
          {percentage === 1 && "All Time High!"}
          {percentage < 1 && (
            <>{(percentage * 100).toFixed(0)}% of Historical Max</>
          )}
        </Badge>
      </Group>

      <Space h={"xs"} />
      <Stack>
        <Box>
          <Title order={5}>Current Offers</Title>
          {currentOffers.length === 0 && (
            <Text fz={"sm"}>
              No offers currently. This usually means an offer expired
              recently;` I{"'"}ll get a new one added soon!
            </Text>
          )}
          <Stack>
            {currentOffers
              .sort((a, b) => {
                return (
                  getOfferFirstYearValue(b, card).value -
                  getOfferFirstYearValue(a, card).value
                );
              })
              .map((offer) => (
                <OfferView
                  key={JSON.stringify(offer)}
                  offer={offer}
                  parent={card}
                />
              ))}
          </Stack>
        </Box>
        {percentage < 1 && (
          <Box>
            <Title order={5}>Historical Bests</Title>
            <Stack>
              {card.historicalOffers
                .filter((offer) => offerMatchesFilters(offer, filters, card))
                .filter(
                  (offer) =>
                    getOfferFirstYearValue(offer, card).value >
                    maxValueOfferDetails.value,
                )
                .map((offer) => (
                  <OfferView
                    key={JSON.stringify(offer)}
                    offer={offer}
                    parent={card}
                  />
                ))}
            </Stack>
          </Box>
        )}
      </Stack>
      {percentage === 1 && (
        <Text fz={"sm"} c={"dimmed"}>
          <i>This is the best offer in recent history.</i>
        </Text>
      )}

      <Anchor
        href={card.url}
        target={"_blank"}
        style={{
          position: "absolute",
          bottom: "15px",
          width: `calc(100% - ${theme.spacing.sm} * 2)`,
        }}
      >
        <Button
          variant="light"
          color="blue"
          fullWidth={true}
          mt="md"
          radius="md"
          onClick={() => {
            va.track("Official Page Click", {
              card: `${card.issuer} ${card.name}`,
            });
          }}
        >
          Official Page <UpRightButton />
        </Button>
      </Anchor>

      <Space h={`${Number.parseInt(theme.spacing.lg) * 2 + 50}px`} />
    </Card>
  );
};
